import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Card, Space, Modal, Typography, Tag,
  Row, Col, Input, Spin, Form, InputNumber, Upload,
  message, Popconfirm, Tooltip, Select, AutoComplete
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  DownloadOutlined, UploadOutlined
} from '@ant-design/icons';
import { useAuth } from '../utils/auth';
import storeService from '../services/storeService';

const { Title, Text } = Typography;
const { Option } = Select;

const Stores = () => {
  const { token } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressSearchLoading, setAddressSearchLoading] = useState(false);
  const [searchTimer, setSearchTimer] = useState(null);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // 分页配置
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
  });

  // 获取门店列表
  const fetchStores = async (page = 1, pageSize = 20, name = '') => {
    try {
      setLoading(true);
      const params = {
        page: page - 1,
        size: pageSize,
        ...(name && { name })
      };
      
      const response = await storeService.getStores(params);
      
      setStores(response.content || []);
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize,
        total: response.totalElements || 0
      }));
    } catch (error) {
      message.error('获取门店列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    fetchStores();
  }, []);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    };
  }, [searchTimer]);

  // 搜索
  const handleSearch = (value) => {
    setSearchText(value);
    fetchStores(1, pagination.pageSize, value);
  };

  // 表格分页变化
  const handleTableChange = (paginationConfig) => {
    fetchStores(paginationConfig.current, paginationConfig.pageSize, searchText);
  };

  // 新建门店
  const handleCreate = () => {
    setEditingStore(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 编辑门店
  const handleEdit = (store) => {
    setEditingStore(store);
    form.setFieldsValue({
      ...store,
      longitude: store.longitude ? parseFloat(store.longitude) : undefined,
      latitude: store.latitude ? parseFloat(store.latitude) : undefined
    });
    setModalVisible(true);
  };

  // 实际的地址搜索函数
  const performAddressSearch = async (keywords) => {
    if (!keywords || keywords.trim().length < 2) {
      setAddressOptions([]);
      setAddressSearchLoading(false);
      return;
    }

    try {
      setAddressSearchLoading(true);
      const response = await storeService.searchAddress(keywords.trim());
      
      const options = response.data.map(tip => ({
        value: tip.address || tip.name,
        label: (
          <div>
            <div style={{ fontWeight: 'bold' }}>{tip.name}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {tip.district} · {tip.address}
            </div>
          </div>
        ),
        tip: tip
      }));
      
      setAddressOptions(options);
    } catch (error) {
      console.error('地址搜索失败:', error);
      setAddressOptions([]);
    } finally {
      setAddressSearchLoading(false);
    }
  };

  // 防抖的地址搜索
  const handleAddressSearch = useCallback((keywords) => {
    // 清除之前的定时器
    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    // 如果输入为空，立即清空选项
    if (!keywords || keywords.trim().length < 2) {
      setAddressOptions([]);
      setAddressSearchLoading(false);
      return;
    }

    // 显示加载状态
    setAddressSearchLoading(true);

    // 设置新的定时器，800ms后执行搜索
    const timer = setTimeout(() => {
      performAddressSearch(keywords);
    }, 800);

    setSearchTimer(timer);
  }, [searchTimer]);

  // 地址选择
  const handleAddressSelect = (value, option) => {
    const tip = option.tip;
    if (tip) {
      form.setFieldsValue({
        address: tip.address, // 后端已经组合了完整地址
        longitude: tip.longitude,
        latitude: tip.latitude
      });
    }
  };

  // 删除门店
  const handleDelete = async (id) => {
    try {
      await storeService.deleteStore(id);
      message.success('删除成功');
      fetchStores(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 保存门店
  const handleSave = async (values) => {
    try {
      // 如果经纬度缺失，尝试通过地址自动解析
      const payload = { ...values };
      const hasLngLat = payload.longitude !== undefined && payload.longitude !== null && payload.latitude !== undefined && payload.latitude !== null;
      if (!hasLngLat && payload.address) {
        try {
          const geo = await storeService.geocodeAddress(payload.address);
          if (geo && geo.longitude != null && geo.latitude != null) {
            payload.longitude = geo.longitude;
            payload.latitude = geo.latitude;
          }
        } catch (e) {
          // 地址解析失败则忽略，继续按原数据保存
          console.error('自动地址解析失败:', e);
        }
      }

      if (editingStore) {
        await storeService.updateStore(editingStore.id, payload);
        message.success('更新成功');
      } else {
        await storeService.createStore(payload);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchStores(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error(editingStore ? '更新失败' : '创建失败');
    }
  };


  // 下载模板
  const handleDownloadTemplate = async () => {
    try {
      await storeService.downloadTemplate();
      message.success('模板下载成功');
    } catch (error) {
      message.error('模板下载失败');
    }
  };

  // 文件上传
  const uploadProps = {
    fileList,
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     file.type === 'application/vnd.ms-excel';
      if (!isExcel) {
        message.error('只能上传Excel文件!');
        return false;
      }
      setFileList([file]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    }
  };

  // 批量导入
  const handleImport = async () => {
    if (fileList.length === 0) {
      message.warning('请选择要导入的文件');
      return;
    }

    try {
      message.info('已提交导入，后台解析需要一定时间，请耐心等待');
      setImporting(true);
      await storeService.importStores(fileList[0]);
      message.success('导入任务已提交，后台正在解析');
      setImportModalVisible(false);
      setFileList([]);
      // 异步执行，无需立即刷新；如需可延时刷新列表
    } catch (error) {
      message.error('导入失败');
    } finally {
      setImporting(false);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '门店名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <span>{text}</span>
        </Space>
      )
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      width: 200,
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
      render: (text) => text || '-'
    },
    {
      title: '经纬度',
      key: 'coordinates',
      width: 120,
      render: (_, record) => {
        if (record.longitude && record.latitude) {
          return (
            <Tooltip title={`经度: ${record.longitude}, 纬度: ${record.latitude}`}>
              <Tag color="blue">已定位</Tag>
            </Tooltip>
          );
        }
        return <Tag color="default">未定位</Tag>;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => {
        const color = status === 'active' ? 'success' : 'default';
        const text = status === 'active' ? '启用' : '禁用';
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (text) => text ? new Date(text).toLocaleString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个门店吗？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>门店管理</Title>
        </Col>
      </Row>
      
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                <Input.Search
                  placeholder="搜索门店名称"
                  enterButton
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onSearch={handleSearch}
                  style={{ width: 200 }}
                  allowClear
                />
              </Space>
            </Col>
          </Row>
          
          <Row>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                >
                  新建门店
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadTemplate}
                >
                  下载模板
                </Button>
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => setImportModalVisible(true)}
                >
                  批量导入
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
        
        <Table
          columns={columns}
          dataSource={stores}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Card>

      {/* 新建/编辑门店模态框 */}
      <Modal
        title={editingStore ? '编辑门店' : '新建门店'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="name"
            label="门店名称"
            rules={[{ required: true, message: '请输入门店名称' }]}
          >
            <Input placeholder="请输入门店名称" />
          </Form.Item>
          
          <Form.Item
            name="address"
            label="门店地址"
            rules={[{ required: true, message: '请输入门店地址' }]}
          >
            <AutoComplete
              placeholder="请输入门店地址，支持地址搜索"
              options={addressOptions}
              onSearch={handleAddressSearch}
              onChange={() => {
                // 用户手动修改地址时，清空经纬度，避免旧坐标与新地址不匹配
                form.setFieldsValue({ longitude: undefined, latitude: undefined });
              }}
              onSelect={handleAddressSelect}
              loading={addressSearchLoading}
              filterOption={false}
              notFoundContent={
                addressSearchLoading ? <Spin size="small" /> : 
                addressOptions.length === 0 ? '暂无搜索结果' : null
              }
            />
          </Form.Item>
          
          {/* 隐藏经纬度字段，但保留用于数据提交 */}
          <div style={{ display: 'none' }}>
            <Form.Item name="longitude">
              <InputNumber />
            </Form.Item>
            <Form.Item name="latitude">
              <InputNumber />
            </Form.Item>
          </div>
          
          <Form.Item
            name="phone"
            label="联系电话"
          >
            <Input placeholder="请输入联系电话" />
          </Form.Item>
          
          
          
          <Form.Item
            name="status"
            label="状态"
            initialValue="active"
          >
            <Select>
              <Option value="active">启用</Option>
              <Option value="inactive">禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量导入模态框 */}
      <Modal
        title="批量导入门店"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        onOk={handleImport}
        confirmLoading={importing}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>请先下载模板，按照模板格式填写门店信息后上传。</Text>
        </div>
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />}>选择文件</Button>
        </Upload>
      </Modal>

    </div>
  );
};

export default Stores;